/**
 * Shared confidence-badge styles used by DocumentExtractionFieldsTab and AiBatchIntake.
 * Both surfaces render a confidence percentage pill; high confidence is green, low is amber.
 */

import { COLOR_PALETTE } from 'constants/ui';

const confidenceBadgeBaseStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: '10px',
  px: '8px',
  py: '2px',
  whiteSpace: 'nowrap',
};

export const confidenceBadgeHighStyles = {
  ...confidenceBadgeBaseStyles,
  // Dark: .light is a saturated green and .dark a mid green — the pill reads as a loud block with
  // low-contrast text. Use a soft tint fill + bright text. Light keeps its original pairing.
  backgroundColor: ({ palette }) =>
    palette.mode === 'dark' ? palette.success.soft : palette.success.light,
  color: ({ palette }) => (palette.mode === 'dark' ? palette.success.light : palette.success.dark),
};

// DEV-4598: WCAG AA contrast fix for the low-confidence amber pill.
//
// Previous light-mode pairing: fill #FEC84B (warning.light) + text #B54708 (warning.dark) ≈ 2.5:1
// — failed WCAG AA (minimum 4.5:1 for small text).
//
// New light-mode pairing: fill warning.extraSoft (#FFFAEB) + text ORANGE[9] (#93370D) ≈ 7.1:1.
// The fill is the palest amber tint so the pill still reads as "low confidence" without the
// saturated yellow slab, and the deep-amber text lands well above the AA threshold.
//
// Dark mode is unchanged: fill warning.soft (low-alpha amber on a near-black surface) with
// warning.main (#FBBF24) text already exceeds AA because the effective blended fill is dark.
export const confidenceBadgeLowStyles = {
  ...confidenceBadgeBaseStyles,
  backgroundColor: ({ palette }) =>
    palette.mode === 'dark' ? palette.warning.soft : palette.warning.extraSoft,
  color: ({ palette }) =>
    palette.mode === 'dark' ? palette.warning.main : COLOR_PALETTE.ORANGE[9],
};

/** Neutral badge for segments where the backend returned null/undefined confidence.
 * Shown as "Not scored" — not flagged amber (low) nor green (high). */
export const confidenceBadgeNeutralStyles = {
  ...confidenceBadgeBaseStyles,
  backgroundColor: ({ palette }) => palette.background.secondary,
  color: ({ palette }) => palette.text.secondary,
};

export const confidenceBadgeRowStyles = {
  display: 'flex',
  justifyContent: 'flex-end',
  mb: '4px',
};
